import { useEffect, useLayoutEffect, useState } from 'react';
import { css } from '@emotion/react';
import { NetworkPerformance } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { TagsV2 } from '@frontend/api-tag';
import { getWeaveToken } from '@frontend/auth-helpers';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber, removeNonDigits } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { theme } from '@frontend/theme';
import { Accordion, Button, Heading, Text, useAlert } from '@frontend/design-system';
import {
  useRecentCallsTagMutation,
  optimisticApplyTag as optimisticCallRecordsApplyTag,
  optimisticRemoveTag as optimisticCallRecordsRemoveTag,
} from '../hooks';
import { AddTag } from '../hooks/recent-calls-tag-selector';
import { recentCallsTrackingId } from '../pendo-tracking';
import { CallDetailsContext, CallDetailsPanelAccordionType } from '../types';
import { getFullName, getRecentCallsUniqueKey } from '../utils';
import {
  CallDetailsSection,
  CallRecordingSection,
  ContactActions,
  ContactSection,
  TagsSection,
} from './call-details-panel-sections';
import { CallQualityAccordionItemOld } from './call-quality-components';
import { PanelHeader } from './common';
import { SCREENS } from './const';
import { ScreenBase } from './types';

export const CallDetailsScreen = ({ setScreen }: ScreenBase) => {
  const { t } = useTranslation('calls');
  const { selectedOrgId, selectedLocationIds, getScopeName } = useAppScopeStore();
  const { show, setShow, context } = useSlidePanelShallowStore<CallDetailsContext>('setShow', 'context', 'show');
  const alerts = useAlert();
  const weaveToken = getWeaveToken();
  const hasAccessToRecordings = !!context?.hasAccessToRecordings;
  const row = context?.rows.find((row) => row.id === context.currentRowId);
  const personId = row?.person?.personId || '';
  const firstName = row?.person?.firstName || '';
  const lastName = row?.person?.lastName || '';
  const personName = getFullName(row?.person?.firstName, row?.person?.lastName);
  const contactNumber = formatPhoneNumber(row?.direction === 'inbound' ? row?.callerNumber : row?.dialedNumber);
  const locationId = row?.locationId;
  const currentRowIndex = context?.rows.findIndex((row) => getRecentCallsUniqueKey(row) === context.currentRowId) ?? 0;
  const hasCallRecording = !!row?.recordingPath && !!row?.recordingFilename;
  const callRecordingPath = !!row
    ? `${appConfig.BACKEND_API}/portal/v1/phone/callRecordings/${row.channelId}?location_id=${locationId}&token=${weaveToken}`
    : '';

  const shouldShowNetworkPerformance =
    row?.networkPerformance === NetworkPerformance.NETWORK_PERFORMANCE_ISSUE_DETECTED ||
    row?.networkPerformance === NetworkPerformance.NETWORK_PERFORMANCE_GOOD;
  const hasIssues = row?.networkPerformance === NetworkPerformance.NETWORK_PERFORMANCE_ISSUE_DETECTED;

  const [openedAccordionId, setOpenedAccordionId] = useState<CallDetailsPanelAccordionType>(
    context?.sectionFocus ?? 'details'
  );

  const { data: tagData } = TagsV2.Queries.useListTagsQuery({
    request: { orgId: selectedOrgId, groupIds: selectedLocationIds },
    options: {
      enabled: row?.tags?.length === 0,
    },
  });

  const mappedTags =
    tagData?.tags.reduce((acc, tag) => {
      acc[tag.id] = tag;
      return acc;
    }, {} as Record<string, (typeof tagData)['tags'][number]>) ?? {};

  // we need to update the tags data if it has been changed either by adding or removing a tag when panel is opened from a notification
  const queriedTagData =
    context?.fetchTagData && !!context?.callDetailsQueryKey ? context?.fetchTagData(context.callDetailsQueryKey) : null;
  const assignedTags =
    !!row && row.tags?.length > 0 ? row.tags : (queriedTagData ?? row?.tagIds)?.map((tagId) => mappedTags[tagId]) || [];

  useLayoutEffect(() => {
    if (show && !!context?.sectionFocus && openedAccordionId !== context.sectionFocus) {
      setOpenedAccordionId(context.sectionFocus);
    }
  }, [openedAccordionId, context?.sectionFocus, show]);

  /**
   * This effect is used to fetch the next page of voicemails when the user reaches the end of the current page of results.
   */
  useEffect(() => {
    if (context && currentRowIndex + 1 >= context.rows.length) {
      console.log('Fetching next page of call details');
      context.getNextPage?.().then((res) => {
        setShow(true, 'callDetails', {
          ...context,
          rows: res.data?.pages.flatMap((page) => page.data || []) ?? [],
          getNextPage: res.data?.pages?.at(-1)?.meta?.hasNext ? context.getNextPage : undefined,
        });
      });
    }
  }, [currentRowIndex, context?.rows.length, context, setShow]);

  const { applyTag, removeTag } = useRecentCallsTagMutation({
    queryKey: context?.callDetailsQueryKey ?? [],
    channelId: row?.channelId ?? '',
    callStartedAt: row?.startedAt ?? '',
    callRecordLocationId: row?.locationId ?? '',
    optimisticApplyTag: context?.optimisticApplyTag ?? optimisticCallRecordsApplyTag,
    optimisticRemoveTag: context?.optimisticRemoveTag ?? optimisticCallRecordsRemoveTag,
    onSuccess: ({ op }) => {
      if (op === 'add') alerts.success(t('Tag added successfully.'));
      if (op === 'remove') alerts.success(t('Successfully removed tag.'));
    },
    onFailure: ({ op }) => {
      if (op === 'add') alerts.error(t('Failed to add tag.'));
      if (op === 'remove') alerts.error(t('Failed to remove tag.'));
    },
  });

  if (!context || !row) {
    return null;
  }

  return (
    <div style={{ display: 'grid', height: '100%', gridTemplateRows: 'auto 1fr auto', overflowY: 'auto' }}>
      <PanelHeader setScreen={setScreen}>
        <Heading level={2} css={{ margin: 0 }}>
          {t('Call Details')}
        </Heading>
      </PanelHeader>
      {row && (
        <div
          style={{
            padding: theme.spacing(3),
          }}
        >
          <ContactSection
            contact={{
              id: personId,
              firstName,
              lastName,
              profileName: personName,
              defaultName: t('Unknown'),
              number: contactNumber,
            }}
            locationId={row.locationId ?? ''}
          >
            <ContactActions
              contact={{ name: personName, id: personId, number: removeNonDigits(contactNumber) }}
              locationId={row.locationId ?? ''}
            />
          </ContactSection>
          {hasCallRecording && hasAccessToRecordings && (
            <CallRecordingSection row={row} callRecordingPath={callRecordingPath} />
          )}

          <Accordion
            variant='blank'
            padding={0}
            controlledValue={openedAccordionId}
            onChange={(value) => {
              setOpenedAccordionId(value as CallDetailsPanelAccordionType);
              setShow(true, 'callDetails', {
                ...context,
                sectionFocus: value as CallDetailsPanelAccordionType,
              });
            }}
            css={css`
              > div:not(:last-child) {
                margin-bottom: ${theme.spacing(2)};
              }
            `}
          >
            <Accordion.Item title={t('About this Call')} value='details' css={styles.accordionItemStyle}>
              <Accordion.Header
                title={t('About this Call')}
                css={styles.accordionHeaderStyle(openedAccordionId, 'details')}
              />
              <Accordion.Body css={{ padding: 0 }}>
                <CallDetailsSection row={row} locationName={getScopeName(row.locationId ?? '')} />
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item title={t('Tags')} value='tags' css={styles.accordionItemStyle}>
              <Accordion.Header css={styles.accordionHeaderStyle(openedAccordionId, 'tags')}>
                <div
                  css={css`
                    display: flex;
                    justify-content: space-between;
                    width: 100%;
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: ${theme.spacing(0.5)};
                    `}
                  >
                    <Text
                      css={css`
                        font-size: ${theme.fontSize(18)};
                      `}
                      weight='bold'
                    >
                      {t('Tags')}
                    </Text>
                    <Text size='small' color='subdued'>
                      {assignedTags?.length > 0 ? assignedTags?.length : ''}
                    </Text>
                  </div>
                  <AddTag existingTagIds={assignedTags?.map((tag) => tag.id)} onApplyTag={applyTag} />
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <TagsSection tags={assignedTags} onApplyTag={applyTag} onRemoveTag={removeTag} />
              </Accordion.Body>
            </Accordion.Item>

            {shouldShowNetworkPerformance && (
              <CallQualityAccordionItemOld
                itemClassname={styles.accordionItemStyle}
                headerClassname={styles.accordionHeaderStyle(openedAccordionId, 'performance')}
                onClick={() => {
                  setScreen({ screen: SCREENS.CALL_QUALITY });
                }}
                hasIssues={hasIssues}
                item={row}
              />
            )}
          </Accordion>
        </div>
      )}
      <div
        css={{
          display: 'flex',
          justifyContent: 'end',
          padding: theme.spacing(0, 3, 3, 3),
          gap: theme.spacing(1),
        }}
      >
        <Button
          variant='secondary'
          size='large'
          trackingId={recentCallsTrackingId({ context: `panel::call-details::previous` })}
          disabled={currentRowIndex - 1 < 0}
          onClick={() => {
            if (!!context) {
              setShow(true, 'callDetails', {
                currentRowId: !!context?.rows ? getRecentCallsUniqueKey(context?.rows?.[currentRowIndex - 1]) : '',
                rows: context.rows,
                hasAccessToRecordings,
                instanceId: context.instanceId,
                toggleRowSelected: context.toggleRowSelected,
                callDetailsQueryKey: context.callDetailsQueryKey,
                sectionFocus: context.sectionFocus,
                getNextPage: context.getNextPage,
              });
            }
          }}
        >
          {t('Previous')}
        </Button>
        <Button
          variant='secondary'
          size='large'
          trackingId={recentCallsTrackingId({ context: `panel::call-details::next` })}
          disabled={currentRowIndex === (context?.rows?.length || 0) - 1}
          onClick={() => {
            if (!!context) {
              setShow(true, 'callDetails', {
                currentRowId: !!context?.rows ? getRecentCallsUniqueKey(context?.rows?.[currentRowIndex + 1]) : '',
                rows: context.rows,
                hasAccessToRecordings,
                instanceId: context.instanceId,
                toggleRowSelected: context.toggleRowSelected,
                callDetailsQueryKey: context.callDetailsQueryKey,
                sectionFocus: context.sectionFocus,
                getNextPage: context.getNextPage,
              });
            }
          }}
        >
          {t('Next')}
        </Button>
      </div>
    </div>
  );
};

const styles = {
  accordionHeaderStyle: (
    openedAccordionId: CallDetailsPanelAccordionType,
    currentAccordionType: CallDetailsPanelAccordionType
  ) => css`
    background-color: ${openedAccordionId === currentAccordionType ? theme.colors.neutral5 : 'transparent'};
    border-bottom: none;
    padding: ${theme.spacing(2, 0, 2, 2)};
  `,

  accordionItemStyle: css`
    border-radius: ${theme.spacing(1)};
    border: 1px solid ${theme.colors.neutral20};
    > div {
      display: block !important;
    }
    :last-child {
      border-bottom: 1px solid ${theme.colors.neutral20};
    }
  `,
};
